/**
 * Orders API (api-contract.md §3.4 patient + §5 pharmacist).
 * Patient: place/list/get/cancel. Pharmacist: queue + the confirm→price→publish workflow.
 */

import { apiFetch, downloadFile, newIdempotencyKey } from "./client";

// ----- Shared shapes -----

export interface OrderLine {
  id: string;
  prescription_line_id: string;
  medicine_id: string;
  medicine_name: string;
  quantity_prescribed: number;
  is_available: boolean;
  quantity_dispensed: number;
  quantity_remark: string | null;
  mrp_per_unit: string;
  taxable_value: string;
  cgst_amount: string;
  sgst_amount: string;
  igst_amount: string;
  display_order: number;
}

/** The published price is being revised: the patient can't pay until it is re-published, the
 *  edit is cancelled, or the window ends (backend PRICE_EDIT_WINDOW_MINUTES). */
export interface PriceRevision {
  expires_at: string;
  seconds_left: number; // at response time — count down from this, not from the device clock
}

/** The same, for the pharmacist: who is editing. */
export interface PharmacistPriceRevision extends PriceRevision {
  revision_id: string;
  started_by_user_id: string;
  started_by_name: string | null;
  started_at: string;
}

/** The order's latest payment attempt, as order payloads carry it. */
export interface OrderPayment {
  attempt_id: string;
  status: string; // pending | succeeded | failed | cancelled | expired
  amount: string;
  failure_reason: string | null;
  rzp_payment_id: string | null;
  initiated_at: string;
  expires_at: string;
  completed_at: string | null;
}

export interface Order {
  id: string;
  /** Human-facing reference (ORD000001). `id` stays the UUID used in route paths. */
  order_id: string;
  prescription_id: string;
  patient_account_id: string;
  for_family_member_id: string | null;
  status_code: string;
  status_label: string;
  delivery_address_snapshot: Record<string, unknown>;
  placement_note: string | null;
  assignee_pharmacist_id: string | null;
  address_confirmed_at: string | null;
  pricing_published_at: string | null;
  medicines_subtotal: string;
  delivery_charge: string;
  gst_total: string;
  final_amount: string;
  paid_at: string | null;
  cancelled_at: string | null;
  lines: OrderLine[];
  created_at: string;
  /** The latest cancel-and-refund request on a paid order (ORD-16). */
  cancel_request?: CancelRequest | null;
  /** The patient may ask to cancel and be refunded now: paid, not yet dispatched, none open. */
  can_request_cancel?: boolean;
  /** What a refund gives back: the amount paid less GST and fees (D-14). */
  refund_estimate?: string | null;
  /** The latest refund while the order is being or has been refunded (ORD-17). */
  refund?: OrderRefund | null;
  /** The latest payment attempt (ORD-22). */
  payment?: OrderPayment | null;
  /** Set while the pharmacy revises the published price: paying is paused (ORD-22). */
  price_revision?: PriceRevision | null;
}

export interface OrderRefund {
  amount: string;
  status: string; // initiated | processed | failed
  initiated_at: string;
  processed_at: string | null;
}

export interface CancelRequest {
  id: string;
  status: "open" | "refunded" | "declined";
  reason: string;
  created_at: string;
  resolved_at: string | null;
  decline_note: string | null;
}

export interface OrderSummary {
  id: string;
  order_id: string;
  prescription_id: string;
  for_family_member_id: string | null;
  status_code: string;
  status_label: string;
  final_amount: string;
  /** Set once the pharmacy publishes the price (ORD-10); absent from an older API. */
  pricing_published_at?: string | null;
  line_count: number;
  created_at: string;
  price_revision?: PriceRevision | null;
}

export interface PaginatedOrders {
  data: OrderSummary[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

/** Another pharmacist's open request to take over a claimed order (PHA-03). Until `effective_at`
 * only the claimant can change the order and may keep it; then it passes to the requester. */
export interface ClaimTakeover {
  takeover_id: string;
  requested_by_user_id: string;
  requested_by_name: string | null;
  from_user_id: string;
  requested_at: string;
  effective_at: string;
  /** At response time — count down from this, not from the device clock. */
  seconds_left: number;
}

/** A pharmacist queue card: the summary plus who ordered it (absent on a row added from a push). */
export interface QueueOrder extends OrderSummary {
  patient_name?: string | null;
  patient_phone?: string | null;
}

export interface PaginatedQueue {
  data: QueueOrder[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

/** What the server's GST computation depends on, so the page can preview totals exactly. */
export interface PricingBasis {
  intra_state: boolean; // CGST + SGST (half the rate each) when true, else IGST
  delivery_gst_pct: string;
  line_gst_pct: Record<string, string>; // order line id → GST rate (%)
}

export interface PharmacistOrder extends Order {
  warnings: { is_first_order: boolean; is_zero_purchase: boolean; address_changed: boolean };
  price_revision?: PharmacistPriceRevision | null;
  schedule_class: string;
  patient_name: string;
  patient_phone: string | null;
  /** Who has claimed the order (absent from API builds before PHA-03). */
  assignee_name?: string | null;
  claim_takeover?: ClaimTakeover | null;
  pricing_basis: PricingBasis;
  claimed_at: string | null;
  prescription: OrderPrescriptionInfo | null;
}

/** Where the order's prescription came from (pharmacist order history). */
export interface OrderPrescriptionInfo {
  ref: string;
  /** "doctor": written in the app; "upload": digitised from the patient's scanned paper copy. */
  source: "doctor" | "upload";
  issued_at: string;
  doctor_name: string | null;
}

// ----- Patient (P-09/10/11) -----

export interface PlaceOrderInput {
  prescription_id: string;
  delivery_address_id?: string;
  delivery_address_inline?: Record<string, unknown>;
  save_address_to_book?: boolean;
  placement_note?: string;
}

export const placeOrder = (body: PlaceOrderInput, idempotencyKey?: string): Promise<Order> =>
  apiFetch("/api/v1/me/orders", { method: "POST", body, idempotencyKey: idempotencyKey ?? newIdempotencyKey() });

export const listMyOrders = (page = 1, per_page = 25): Promise<PaginatedOrders> =>
  apiFetch(`/api/v1/me/orders?page=${page}&per_page=${per_page}`);

export const getMyOrder = (id: string): Promise<Order> => apiFetch(`/api/v1/me/orders/${id}`);

export const cancelMyOrder = (id: string, reason?: string): Promise<Order> =>
  apiFetch(`/api/v1/me/orders/${id}/cancel`, { method: "POST", body: { reason: reason ?? null } });

/** A paid order, before dispatch: ask to cancel and be refunded; admins decide (ORD-16, D-05). */
export const requestOrderCancel = (id: string, reason: string): Promise<Order> =>
  apiFetch(`/api/v1/me/orders/${id}/cancel-request`, { method: "POST", body: { reason } });

// ----- Pharmacist (Rx-01..06) -----

/** Extra queue filters: `scope` splits Mine into ongoing / completed; `status` is a status code;
 *  `created_from` / `created_to` bound the order time (ISO, end exclusive). */
export interface QueueFilters {
  scope?: "ongoing" | "completed";
  status?: string;
  created_from?: string;
  created_to?: string;
}

/** `q` matches the order reference, patient name or phone — a partial match, so "12" finds ORD-12. */
export const listQueue = (
  tab: "mine" | "unassigned" | "all",
  page = 1,
  per_page = 25,
  q = "",
  filters: QueueFilters = {},
): Promise<PaginatedQueue> => {
  const query = new URLSearchParams({ tab, page: String(page), per_page: String(per_page) });
  if (q.trim()) query.set("q", q.trim());
  for (const [key, value] of Object.entries(filters)) if (value) query.set(key, value);
  return apiFetch(`/api/v1/pharmacist/queue?${query}`);
};

export const getPharmacistOrder = (id: string): Promise<PharmacistOrder> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}`);

/** The order's medicines (with prices once published) as a file to share. `orderRef` names it.
 *  Each format spells out its own path so check:api can match it to the spec. */
export const downloadOrderItems = (id: string, orderRef: string, format: "xlsx" | "pdf"): Promise<void> =>
  format === "xlsx"
    ? downloadFile(`/api/v1/pharmacist/orders/${id}/items.xlsx`, `${orderRef}-items.xlsx`)
    : downloadFile(`/api/v1/pharmacist/orders/${id}/items.pdf`, `${orderRef}-items.pdf`);

export const claimOrder = (id: string): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}/claim`, { method: "POST" });

export const releaseClaim = (id: string): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}/release-claim`, { method: "POST" });

/** Ask the claimant to hand the order over; it passes to you unless they keep it in time. */
export const requestTakeover = (id: string): Promise<PharmacistOrder> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}/takeover`, { method: "POST" });

/** The claimant keeps the order (turns the takeover request down). */
export const rejectTakeover = (id: string): Promise<PharmacistOrder> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}/takeover/reject`, { method: "POST" });

/** The requester drops their takeover request. */
export const withdrawTakeover = (id: string): Promise<PharmacistOrder> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}/takeover/withdraw`, { method: "POST" });

export const confirmAddress = (id: string, confirmation_phrase: string): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${id}/confirm-address`, {
    method: "POST",
    body: { confirmation_phrase },
  });

export const setLineAvailability = (
  orderId: string,
  lineId: string,
  is_available: boolean,
): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${orderId}/lines/${lineId}`, {
    method: "PATCH",
    body: { is_available },
  });

export interface PublishPriceInput {
  schedule_signoff_last4: string | null;
  lines: {
    line_id: string;
    quantity_dispensed: number;
    quantity_remark: string | null;
    mrp_per_unit: string;
    /** Re-publish: availability set in the same step (false → send 0 quantity and MRP). */
    is_available?: boolean;
  }[];
  delivery_charge: string;
  /** The total shown to the pharmacist; the server publishes only if it computes the same. */
  expected_final_amount: string;
}

/** Saves the pricing, re-checks the total and publishes — one transaction (all or nothing). */
export const publishPrice = (orderId: string, body: PublishPriceInput): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${orderId}/publish-price`, {
    method: "POST",
    body,
  });

/** Edit the published price (or resume my running edit): the patient can't pay until it is
 *  re-published with publishPrice, cancelled, or the window ends. */
export const startPriceEdit = (orderId: string): Promise<PharmacistOrder> =>
  apiFetch(`/api/v1/pharmacist/orders/${orderId}/price-edit`, { method: "POST" });

/** A delivery address the patient gave on the confirmation call (PHA-21, D-23). Only the claimant,
 *  only until the price is published; the address then has to be confirmed again. */
export interface DeliveryAddressInput {
  name: string;
  phone: string;
  line1: string;
  line2?: string | null;
  landmark?: string | null;
  city: string;
  state_code: string;
  pin_code: string;
  reason?: string | null;
}

export const reviseDeliveryAddress = (orderId: string, body: DeliveryAddressInput): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${orderId}/delivery-address`, { method: "PUT", body });

// Documents (PHA-21, D-23): the prescription as a PDF, and the order's medicines (with prices once
// published) as a spreadsheet or PDF to share.
export const downloadRxPdf = (orderId: string, orderRef: string): Promise<void> =>
  downloadFile(`/api/v1/pharmacist/orders/${orderId}/rx-pdf`, `${orderRef}-rx.pdf`);
/** Drop my edit without changes: the patient can pay the published price again. */
export const cancelPriceEdit = (orderId: string): Promise<PharmacistOrder> =>
  apiFetch(`/api/v1/pharmacist/orders/${orderId}/price-edit/cancel`, { method: "POST" });

// Post-payment fulfilment (Rx-07). Each call advances the order one step; the backend asserts the
// current status, so the UI just maps the order's status_code to the next action.
const advance = (orderId: string, action: string): Promise<Order> =>
  apiFetch(`/api/v1/pharmacist/orders/${orderId}/${action}`, { method: "POST" });

export const startPreparing = (orderId: string): Promise<Order> => advance(orderId, "start-preparing");
export const markReadyForDispatch = (orderId: string): Promise<Order> => advance(orderId, "ready-for-dispatch");
export const dispatchOrder = (orderId: string): Promise<Order> => advance(orderId, "dispatch");
export const deliverOrder = (orderId: string): Promise<Order> => advance(orderId, "deliver");
